
import React, { useEffect, useState, useCallback,useRef ,useContext } from "react";
import Context from "context/context";
import { toast } from "react-toastify";
import * as moment from "moment";
import {
  Grid,
  Paper,
  Typography,
  Box,
  CircularProgress,
} from "@material-ui/core";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";

import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ResponsiveContainer,
  LabelList
} from "recharts";
import { useGoogleAnalyticsAPI } from "../../../apis/googleAnalyst/googleAnalyst";
import { CSVLink } from 'react-csv';
import { Styles } from "./styles";
import { exportHeaders } from "./export";



// Chart Colors
const COLORS = ["#2F7CBD", "#00C49F", "#FFBB28", "#FF8042", "#8884d8"];

// =====================
// Format Helpers
// =====================
function formatDuration(seconds) {
  if (!seconds || isNaN(seconds)) return "0s";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return [h > 0 ? `${h}h` : null, m > 0 ? `${m}m` : null, `${s}s`]
    .filter(Boolean)
    .join(" ");
}

function formatPercentage(value) {
  if (value == null || isNaN(value)) return "0%";
  return `${(value * 100).toFixed(2)}%`;
}

function formatNumber(value, decimals = 2) {
  if (value == null || isNaN(value)) return "0";
  return Number(value).toFixed(decimals);
}

function formatInteger(value) {
  if (value == null || isNaN(value)) return "0";
  return Math.round(value).toLocaleString();
}


// Metric Card
// const MetricCard = ({ title, value }) => (
//   <Paper style={{ padding: title=='Active Users'?25:title=='New Users'?25:16, textAlign: "center", }}>
//     <Typography variant="subtitle2" style={{ color: "#666" }}>
//       {title}
//     </Typography>
//     <Typography variant="h6" style={{ color: "#2F7CBD" ,marginTop:title=='Active Users'?15:title=='New Users'?16:0}}>
//       {value}
//     </Typography>
//   </Paper>
// );
// Metric Card
const MetricCard = ({ title, value }) => (
  <Paper
    style={{
        padding: title === "Avg. Session Duration" ? "16px 6px" : "16px",
      textAlign: "center",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      borderRadius: 12,
    }}
  >
    <Typography variant="subtitle2" style={{ color: "#666" }}>
      {title}
    </Typography>

    <Typography variant="h6" style={{ color: "#2F7CBD" }}>
      {value}
    </Typography>
  </Paper>
);

// Chart Wrapper
const ChartCard = ({ title, children }) => (
  <Paper style={{ padding: 20, height: "100%" }}>
    <Typography variant="h6" gutterBottom style={{ color: "#2F7CBD" }}>
      {title}
    </Typography>
    <div style={{ height: 300 }}>{children}</div>
  </Paper>
);

export default function GADashboard() {
  const [loading, setLoading] = useState(true);
  const { state, dispatch } = useContext(Context);

  const today = new Date();
  const last30 = new Date();
  last30.setDate(today.getDate() - 30);
  const classes = Styles();
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState(today);
  const [minDate, setMinDate] = useState(last30);


  const [metrics, setMetrics] = useState([]);
  const [countryData, setCountryData] = useState([]);
  const [browserData, setBrowserData] = useState([]);
  const [eventData, setEventData] = useState([]);
  const [sessionTrendData, setSessionTrendData] = useState([]);
  const [trafficSources, setTrafficSources] = useState([]);
  const [deviceCategory, setDeviceCategory] = useState([]);
  const [pageView, setPageView] = useState([]);

  // Growth Bonus monthly data
  const [growthBonusData, setGrowthBonusData] = useState([]);

  const [exportData, setExportData] = useState([]);





  const {
    getSessionDurationAPI,
    getEngagedSessionsAPI,
    getNewUsersAPI,
    getViewsPerSessionAPI,
    getUsersByCountryAPI,
    getTopBrowsersAPI,
    getEventsAPI,
    getClicksPerPageAPI,
    getTrafficSourcesAPI,
    getDeviceCategoryAPI,
    getPageViewsAPI,
    getActiveUsersAPI,
    getStartDateAPI
  } = useGoogleAnalyticsAPI();

  const normalizeData = (data, labelKey, valueKey) => {
    if (!Array.isArray(data)) return [];
    
    // Helper function to capitalize the first letter of each word (separated by spaces)
    const toTitleCase = (str) => {
      return str
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    };
    
    return data.map((item, idx) => {
      const rawName = item[labelKey] || `Unknown ${idx}`;
      return {
        name: toTitleCase(rawName),
        value: Number(item[valueKey]) || 0,
      };
    });
  };
  

  // Special Normalizer for Trends (line chart)
 
  const fetchStartDate = useCallback(async () => {
    try {
      const res = await getStartDateAPI();
      const minimumDate = res?.minimumDate || moment().format("YYYY-MM-DD");
      setStartDate(minimumDate);
      setMinDate(minimumDate);
 
      return minimumDate; // return so fetchData can use it
    } catch (err) {
      console.error("Error fetching start date:", err);
      const thirtyDaysAgo = moment().subtract(30, "days").format("YYYY-MM-DD");
      setStartDate(thirtyDaysAgo);
      setMinDate(thirtyDaysAgo);

      return thirtyDaysAgo;
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {


      const start = moment(startDate).format("YYYY-MM-DD");
      const end = moment(endDate).format("YYYY-MM-DD");

      const [
        sessionDuration,
        engagedSessions,
        newUsers,
        viewsPerSession,
        browsers,
        events,
        traffic,
        devices,
        pathViews,
        activeUsers
      ] = await Promise.all([
        getSessionDurationAPI(start, end),
        getEngagedSessionsAPI(start, end),
        getNewUsersAPI(start, end),
        getViewsPerSessionAPI(start, end),
        getTopBrowsersAPI(start, end),
        getEventsAPI(start, end),
        getTrafficSourcesAPI(start, end),
        getDeviceCategoryAPI(start, end),
        getPageViewsAPI(start, end),
        getActiveUsersAPI(start, end)

      ]);

  
let oneweb = state.userInfo?.loyalty_organization_name === "Henry Schein Dental" ?process.env.REACT_APP_ONE_WEB_US:process.env.REACT_APP_ONE_WEB_CA;
let gep = state.userInfo?.loyalty_organization_name === "Henry Schein Dental" ?process.env.REACT_APP_GEP_US:process.env.REACT_APP_GEP_CA;

      const grouped = {};
      // (traffic?.data || [])
      //   .filter(d => {
      //     const ref = d.referrerUrl?.toLowerCase() || "";
      //     return (
      //       ref.includes(oneweb) ||
      //       ref.includes(gep)
      //     );
      //   })
      //   .forEach(d => {
      //     const ref = d.referrerUrl?.toLowerCase() || "";
      //     let sourceMedium = "";
      
      //     if (ref.includes(oneweb)) {
      //       sourceMedium = "Oneweb";
      //     } else if (ref.includes(gep)) {
      //       sourceMedium = "GEP";
      //     }
      
      //     const sess = Number(d.sessions) || 0;
      
      //     grouped[sourceMedium] = (grouped[sourceMedium] || 0) + sess;
      //   });
      
      // Convert grouped object to array
      // const resultArray = Object.entries(grouped).map(([sourceMedium, sessions]) => ({
      //   sourceMedium,
      //   sessions
      // }));
      let traffickeywords=[
        'oneweb_link',
        'gep_link'  
      ]
      let trafficData =(events?.data||[]).filter(
        item => traffickeywords.includes(item.event)
      );
      let resultArray
      console.log(resultArray,"this is teh resut")
      resultArray=trafficData?.map((d) => ({
        sourceMedium: d.event=='oneweb_link'?'Oneweb':'GEP',
        sessions: Number(d.count),
      })) || []
      setTrafficSources(resultArray);

     setMetrics([
  {
    title: "Avg. Session Duration",
    value:
      resultArray?.length > 0
        ? formatDuration(sessionDuration?.averageSessionDuration)
        : 0,
  },
  {
    title: "Engaged Sessions",
    value:
      resultArray?.length > 0
        ? formatInteger(engagedSessions?.engagedSessions)
        : 0,
  },
  {
    title: "Active Users",
    value:
      resultArray?.length > 0
        ? formatInteger(activeUsers?.activeUsers)
        : 0,
  },
  {
    title: "New Users",
    value:
      resultArray?.length > 0
        ? formatInteger(newUsers?.newUsers)
        : 0,
  },
  {
    title: "Views per Session",
    value:
      resultArray?.length > 0
        ? formatInteger(viewsPerSession?.viewsPerSession)
        : 0,
  },
]);
 

      setBrowserData(resultArray?.length>0?normalizeData(browsers?.data, "browser", "activeUsers"):[]);
      let event = []
      let eventkeywords=[
        'page_view',
        'session_start',
        'scroll',
        'click',
        'user_engagement',
        'first_visit'   
      ]
      let screenEvent=[]
      let screeneventkeywords=[
        'link_click_promotions',
        'link_click_benefits'          
  
      ]
     
   
      event=resultArray?.length>0 ? (events?.data||[]).filter(
        item => eventkeywords.includes(item.event)
      ):[]

      // Event Data fix
      setEventData(
        event?.map((d) => ({
          event: d.event,
          count: Number(d.count),
        })) || []
      );
      
    
      
      

      setDeviceCategory(resultArray?.length>0?normalizeData(devices?.data, "deviceCategory", "activeUsers"):[]);
      screenEvent=(events?.data||[]).filter(
        item => screeneventkeywords.includes(item.event)
      );
      screenEvent = screenEvent?.map((d) => ({
        pagePath: d.event,
        pageViews: Number(d.count),
      })) || []

      let pages = []
     

      let keywords=[
        '/dashboard/mydashboard',
      '/dashboard/dashboard',
      '/dashboard/redeem',
      '/dashboard/authorized-members',
      '/dashboard/locations',
      '/dashboard/view-shipto',
      '/dashboard/statements',
      '/dashboard/dormant-view',
      'https://www.henryschein.com/us-en/Dental/thrive-rewards/thrive-rewards-promotions.aspx',
      'https://www.henryschein.com/us-en/Dental/thrive-rewards/thrive-rewards-program-benefits.aspx'
    ]

      /*
       * Growth Bonus is NOT added to the normal Page View chart.
       * It is taken from the same pathViews data and grouped by month.
       */
    const growthBonusPages = (pathViews?.data || []).filter(item =>
  item.pagePath?.toLowerCase().includes('/dashboard/growth-bonus')
);

const monthlyGrowthBonus = {};

growthBonusPages.forEach(item => {
  if (!item.date) {
    return;
  }

  const monthKey = moment(item.date, "YYYYMMDD").format("YYYY-MM");
  const month = moment(item.date, "YYYYMMDD").format("MMM YYYY");

  if (!monthlyGrowthBonus[monthKey]) {
    monthlyGrowthBonus[monthKey] = {
      monthKey,
      month,
      pageViews: 0
    };
  }

  monthlyGrowthBonus[monthKey].pageViews += Number(item.pageViews) || 0;
});

setGrowthBonusData(
  Object.values(monthlyGrowthBonus).sort((a, b) =>
    a.monthKey.localeCompare(b.monthKey)
  )
);
      if(pathViews?.data?.length >0){
        pages= pathViews?.data?.filter(item =>
          keywords.some(keyword =>
            item.pagePath.toLowerCase().includes(keyword.toLowerCase())
          )
        );
        pages = [...pages,...screenEvent]

        const routeMap = {
          "/dashboard/mydashboard":"My Dashboard",
          "/dashboard/dashboard":"View My Activity",
          "/dashboard/redeem":"Redeem My Points",
          "/dashboard/authorized-members":"Authorized Members",
          "/dashboard/locations":"Manage My Account",
          "/dashboard/view-shipto":"Manage My Account",
          "/dashboard/statements":"View My Statements",
          "/dashboard/dormant-view":"Manage My Account",
          "link_click_promotions":"Promotions",
          "link_click_benefits":"View My Benefits"
        };
         
        function groupExactRoutes(datafilter) {
          const result = {};
         
          datafilter.forEach(item => {
            const shortName = routeMap[item.pagePath]; // exact match required
            if (shortName) {
              result[shortName] = (result[shortName] || 0) + item.pageViews;
            }
          });
         
          return Object.entries(result).map(([pagePath, pageViews]) => ({ pagePath, pageViews }));
        }
        pages=groupExactRoutes(pages)
      
      }
      console.log(resultArray?.length,"resultArray?.length")
      setPageView(resultArray?.length>0?normalizeData(pages, "pagePath", "pageViews"):[]);

    } catch (err) {
      console.error("Dashboard API Error:", err);
      // toast.error("Failed to fetch dashboard data");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    const initializeDashboard = async () => {
      const start = await fetchStartDate();

    };
    initializeDashboard();
  }, [fetchStartDate ]);

  useEffect(() => {
    if(startDate){
      fetchData();    
    }
  }, [fetchData]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <CircularProgress />
      </Box>
    );
  }

  // Date Handlers
  const handleStartDateChange = (date) => {
    if (!date || !moment(date).isValid()) {
      toast.error("Invalid start date.");
      setStartDate(new Date());
      return;
    }
    if (endDate && moment(date).isAfter(endDate)) {
      toast.error("Start date cannot be after End date.");
      return;
    }
    setStartDate(date);
  };

const handleExport = async () => {
  let rows = [];

  // 1️⃣ Metrics
  rows.push(["Start Date", "End Date"]);
  rows.push([
    moment(startDate).format("YYYY-MM-DD"),
    moment(endDate).format("YYYY-MM-DD")
  ]);
  rows.push([]);

  rows.push(["Metrics"]);
  metrics.forEach(m => {
    rows.push([m.title, m.value]);
  });
  rows.push([]);

  // 2️⃣ Traffic Sources
  rows.push(["Users by Platform"]);
  rows.push(["Source / Medium", "Sessions"]);
  trafficSources.forEach(t => {
    rows.push([t.sourceMedium, t.sessions]);
  });
  rows.push([]);

  // 3️⃣ Browser Usage
  rows.push(["Browser Usage"]);
  rows.push(["Browser", "Count"]);
  browserData.forEach(b => {
    rows.push([b.name, b.value]);
  });
  rows.push([]);

  // 4️⃣ Page Views
  rows.push(["Page Views"]);
  rows.push(["Page", "Count"]);
  pageView.forEach(p => {
    rows.push([p.name, p.value]);
  });
  rows.push([]);



  // 6️⃣ Events
  rows.push(["Event Types Breakdown"]);
  rows.push(["Event", "Count"]);
  eventData.forEach(e => {
    rows.push([eventLabelsY[e.event], e.count]);
  });
  rows.push([]);

  // 7️⃣ Device Category
  rows.push(["Device Category"]);
  rows.push(["Device", "Count"]);
  deviceCategory.forEach(d => {
    rows.push([d.name, d.value]);
  });
  rows.push([]);

    // 5️⃣ Unlock Growth Bonus
  rows.push(["Unlock Growth Bonus"]);
  rows.push(["Month", "Count"]);
  growthBonusData.forEach(g => {
    rows.push([g.month, g.pageViews]);
  });
  rows.push([]);
  setExportData(rows);

  return rows;
};
   

  const handleEndDateChange = (date) => {
    if (!date || !moment(date).isValid()) {
      toast.error("Invalid end date.");
      setEndDate(new Date());
      return;
    }
    if (startDate && moment(startDate).isAfter(date)) {
      toast.error("End date cannot be before Start date.");
      return;
    }
    setEndDate(date);
  };

  const handleExport1 = () => {
   
    document.getElementById('csv').click()
    
  };

  const eventLabelsX = {
    page_view: "Page View",
    session_start: "Session Start",
    scroll: "Scroll",
    click: "Click",
    user_engagement: "User Engagement",
    first_visit:"First Visit"
  };

  const eventLabelsY = {
    page_view: "Page View",
    session_start: "Session Start",
    scroll: "Scroll",
    click: "Click",
    user_engagement: "User Engagement",
    first_visit:"First Visit"
  };

  return (
    <Box p={3}>
      <Box 
  display="flex" 
  justifyContent="center" 
  alignItems="center" 
  p={3}          // padding all around 
  bgcolor="#f5f5f5" // optional background color 
  borderRadius={2}   // optional rounded corners 
  mb={1}       // margin bottom 
> 
  <Typography variant="h4" fontWeight="bold"> 
  Analytics Overview 
  </Typography> 
</Box> 

    
      <Grid 
  container 
  spacing={3} 
  justifyContent="center" // align items horizontally 
  alignItems="center"     // align vertically 
  style={{ marginTop: 10 }} 
  bgcolor="#f5f5f5" // optional background color 
  borderRadius={2}   // optional rounded corners 
  mb={1}       // margin bottom 
> 
<div style={{display:"flex",justifyContent:'space-between',width:"60%",paddingLeft:40,paddingRight:14,borderRadius:4,backgroundColor:'#f5f5f5',marginBottom:40}}> 

  <Grid item> 
    <MuiPickersUtilsProvider utils={DateFnsUtils}> 
      <KeyboardDatePicker 
        disableToolbar 
        variant="inline" 
        format="MM/dd/yyyy" 
        margin="normal" 
        label="Start Date" 
        value={startDate} 
        maxDate={new Date()} 
        minDate={minDate} 
        onChange={handleStartDateChange} 
        KeyboardButtonProps={{ "aria-label": "change start date" }} 
        autoOk 
      /> 
    </MuiPickersUtilsProvider> 
  </Grid> 

  <Grid item> 
    <MuiPickersUtilsProvider utils={DateFnsUtils}> 
      <KeyboardDatePicker 
        disableToolbar 
        variant="inline" 
        format="MM/dd/yyyy" 
        margin="normal" 
        label="End Date" 
        value={endDate} 
        maxDate={new Date()} 
        minDate={startDate} 
        onChange={handleEndDateChange} 
        KeyboardButtonProps={{ "aria-label": "change end date" }} 
        autoOk 
      /> 
    </MuiPickersUtilsProvider> 
  </Grid> 

  <Grid item> 
    <CSVLink 
      id="csv" 
      data={exportData} 
      filename={`Google-Analytics ${moment().format("MM-DD-YYYY,h:mm A")}.csv`} 
      className={classes.CSVlink} 
    /> 
  </Grid> 
  

  {/* Export Button */} 
  <Grid item> 
    <button 
      className={classes.exportBtn1} 
      onClick={async () => { 
        let res = await handleExport(); 
        if (res) { 
          handleExport1(); 
        } 
      }} 
    > 
      Export 
    </button> 
  </Grid> 
  </div> 
</Grid> 


      {/* Metrics Summary */} 
 <Grid container spacing={6} justifyContent="center">
  {metrics.map((metric, idx) => (
    <Grid item xs={12} sm={5} md={2} key={idx}>
      <Box
        sx={{
          height: 100,
          width: "100%",
          display: "flex",
          alignItems: "stretch"
        }}
      >
        <MetricCard
          title={metric.title}
          value={metric.value}
        />
      </Box>
    </Grid>
  ))}
</Grid>


      {/* Country & Browser */} 
      <Grid container spacing={3} style={{ marginTop: 20 }}> 
        <Grid item xs={12} md={6}> 
          <ChartCard title="Users by Platform"> 
            <ResponsiveContainer width="100%" height="100%"> 
              <PieChart> 
                <Pie 
                  data={trafficSources} 
                  dataKey="sessions" 
                  nameKey="sourceMedium" 
                  outerRadius={90} 
                  label 
                > 
                  {trafficSources.map((entry, idx) => ( 
                    <Cell key={idx} fill={COLORS[idx % COLORS.length]} /> 
                  ))} 
                </Pie> 
                <Tooltip /> 
                <Legend  
      formatter={(value, entry, index) => ( 
        <span style={{ color: "#000", fontSize: 17 }}> 
          {value} 
        </span> 
      )} 
    /> 
              </PieChart> 
            </ResponsiveContainer> 
          </ChartCard> 
        </Grid> 

        <Grid item xs={12} md={6}> 
          <ChartCard title="Browser Usage"> 
            <ResponsiveContainer width="100%" height="100%"> 
              <BarChart data={browserData}> 
                <XAxis dataKey="name" tick={{ fontSize: 14}}  /> 
                <YAxis /> 
                <Tooltip /> 
                <Legend  
      formatter={(value, entry, index) => ( 
        <span style={{ color: "#000", fontSize: 17 }}> 
          {value} 
        </span> 
      )} 
    /> 
                <Bar 
                 dataKey="value"  
                  name="User Count"  
                  fill="#2F7CBD" /> 
              </BarChart> 
            </ResponsiveContainer> 
          </ChartCard> 
        </Grid> 
      </Grid> 

      {/* Line Chart - Session Trends */} 
      <Grid container spacing={3} style={{ marginTop: 20 }}> 
        <Grid item xs={12}> 
        {/* <ChartCard title="Page View"> 
            <ResponsiveContainer width="100%" height="100%"> */} 
              {/* <BarChart data={pageView}> 
                <XAxis dataKey="name"  
                tick={{ fontSize: 10}} 
                 /> 
                <YAxis /> 
                <Tooltip /> 
                <Legend  
      formatter={(value, entry, index) => ( 
        <span style={{ color: "#000", fontSize: 10 }}> 
          {value} 
        </span> 
      )} 
    /> 
                <Bar dataKey="value"  name="Number of Visits" fill="#00C49F" /> 
              </BarChart> */} 
         <ChartCard title="Page View"> 
         <ResponsiveContainer width="100%" height="105%"> 
    <BarChart  
      data={pageView}  
      layout="vertical"   // IMPORTANT → vertical bars like your screenshot 
      margin={{ left: 40, right: 20 }} 
    > 
      <XAxis type="number" /> 
      <YAxis  
        type="category"  
        dataKey="name" 
        tickFormatter={(value) => value || value} 
        width={150} // give space for labels 
        tick={{ fontSize: 14 }} 
      /> 

      <Tooltip  
        formatter={(value, name, props) => [ 
          value, 
          props.payload.event 
        ]} 
      /> 
       
      <Legend  
        formatter={(value) => ( 
          <span style={{ color: "#000", fontSize: 17 }}> 
            {value} 
          </span> 
        )} 
      /> 

      <Bar  
        dataKey="value"  
        fill="#2F7CBD" 
         name="Page view Count" 

      > 
        {/* ⭐ VALUE INSIDE BAR */} 
        {/* <LabelList  
          dataKey="value"  
          position="insideRight" // inside the bar like ApexCharts 
          fill="#fff"            // white text 
          fontSize={13} 
        /> */} 
      </Bar> 
    </BarChart> 
  </ResponsiveContainer> 
</ChartCard> 

        </Grid> 
      </Grid> 

      {/* Events & Traffic */} 
      <Grid container spacing={3} style={{ marginTop: 20 }}> 
        <Grid item xs={12}> 
        <ChartCard title="Event Types Breakdown"> 
  <ResponsiveContainer width="100%" height={300}> 
    <BarChart data={eventData}> 
      <XAxis  
        dataKey="event"  
        tickFormatter={(value) => eventLabelsX[value] || value}  
        tick={{ fontSize: 14 }} 
      /> 
      <YAxis /> 
      <Tooltip  
        content={({ active, payload, label }) => { 
          if (active && payload && payload.length) { 
            return ( 
              <div style={{ backgroundColor: '#fff', border: '1px solid #ccc', padding: '10px' }}> 
                <p style={{ margin: 0 }}> 
                  {`${eventLabelsY[label] || label}: ${payload[0].value}`} 
                </p> 
              </div> 
            ); 
          } 
          return null; 
        }} 
      /> 
       
        <Legend  
        formatter={(value, entry, index) => ( 
          <span style={{ color: "#000", fontSize: 17 }}> 
            {value} 
          </span> 
        )} 
      /> 
      <Bar dataKey="count" fill="#FF8042" name="Event Count" /> 
    </BarChart> 
  </ResponsiveContainer> 
</ChartCard> 
        </Grid> 

         
      </Grid> 

      <Grid container spacing={3} style={{ marginTop: 20}}> 

        <Grid item xs={12}> 

          <ChartCard title="Device Category"> 
            <ResponsiveContainer width="100%" height="100%"> 
                 
              <PieChart> 
                <Pie data={deviceCategory} dataKey="value" nameKey="name" outerRadius={90} label> 
                  {deviceCategory.map((entry, idx) => ( 
                    <Cell key={idx} fill={COLORS[idx % COLORS.length]} /> 
                  ))} 
                </Pie> 
                <Tooltip /> 
                <Legend  
      formatter={(value, entry, index) => ( 
        <span style={{ color: "#000", fontSize: 17 }}> 
          {value} 
        </span> 
      )} 
    /> 
              </PieChart> 
            </ResponsiveContainer> 
          </ChartCard> 

        </Grid>
{/* 
        <Grid item xs={12} md={6}> 
<ChartCard title="Unlock Growth Bonus"> 
  <ResponsiveContainer width="100%" height="100%"> 
    <BarChart data={growthBonusData}>
      <XAxis dataKey="month" tick={{ fontSize: 14 }} />
      <YAxis />
      <Tooltip formatter={(value) => [value, "Page View Count"]} />
      <Legend 
        formatter={(value, entry, index) => (
          <span style={{ color: "#000", fontSize: 17 }}>
            {value}
          </span>
        )}
      />
      <Bar
        dataKey="pageViews"
        name="Growth Bonus Count"
        fill="#2F7CBD"
      />
    </BarChart>
  </ResponsiveContainer> 
</ChartCard>

        </Grid> */}

      </Grid> 

    </Box> 
  ); 
} 
